import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  CalendarCheck,
  Search,
  Settings,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import Footer from '@/components/pages/shared/Footer';
import HeadSEO from '@/components/seo/HeadSEO';
import useVideosLanding from '@/components/hooks/useVideosLanding';

const AboutUs = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { videos, loading: videosLoading } = useVideosLanding();

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);

  const goTo = (index: number, dir: 1 | -1) => {
    setDirection(dir);
    setCurrent(index);
  };

  const prev = () => goTo((current - 1 + videos.length) % videos.length, -1);
  const next = () => goTo((current + 1) % videos.length, 1);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white dark:bg-black text-gray-800 dark:text-gray-200">
      {/* ── SEO ───────────────────────────────────────────────────────────── */}
      <HeadSEO
        title="Sobre nosotros — Bookoll | Software de reservas online"
        description="Conoce la misión y el equipo detrás de Bookoll. Ayudamos a negocios y clientes a conectar a través de reservas online sencillas y eficientes. Disponible para cualquier negocio de servicios en España."
        canonical="https://bookoll.com/about"
        ogImage="https://bookoll.com/og-home.jpg"
        ogImageAlt="Sobre nosotros — Bookoll software de reservas online"
      />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden text-white w-full">
        {/* Fondo modo claro */}
        <div
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse at 15% 60%, rgba(40,142,127,0.22) 0%, transparent 50%),
              radial-gradient(ellipse at 85% 20%, rgba(16,50,84,0.5) 0%, transparent 55%),
              linear-gradient(170deg, #103254 0%, #103254 25%, #1a4a6e 45%, #3d7fa0 68%, #a8cfe0 84%, #e8f4f8 95%, #ffffff 100%)
            `,
          }}
        />
        {/* Fondo modo oscuro: negro con resplandor teal, igual que Login */}
        <div
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse at 15% 55%, rgba(40, 142, 127, 0.35) 0%, transparent 55%),
              radial-gradient(ellipse at 85% 15%, rgba(16, 50, 84, 0.45) 0%, transparent 55%),
              linear-gradient(170deg, #050807 0%, #0a1a16 22%, #123a30 42%, #103254 60%, #0a0f14 82%, #000000 100%)
            `,
          }}
        />

        <motion.div
          className="absolute top-10 left-[5%] w-56 h-56 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(40,142,127,0.18) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.9, 0.5] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-4 right-[8%] w-64 h-64 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(255,255,255,0.07) 0%, transparent 70%)',
            filter: 'blur(50px)',
          }}
          animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.75, 0.4] }}
          transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 3,
          }}
        />

        <button
          onClick={handleBack}
          aria-label="Volver atrás"
          className="absolute top-5 left-5 z-20 flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 text-sm text-white hover:bg-white/20 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline font-medium">Volver</span>
        </button>

        <div className="relative z-10 pt-20 pb-28 px-6 flex flex-col items-center text-center max-w-3xl mx-auto">
          <motion.h1
            className="text-4xl md:text-5xl font-bold tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
          >
            {t('aboutUs.heroTitle')}
          </motion.h1>

          <motion.div
            className="mt-4 h-px w-16 mx-auto"
            style={{
              background:
                'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
            }}
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
          />

          <motion.p
            className="mt-4 text-base text-white/70 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.3 }}
          >
            {t('aboutUs.heroSubtitle')}
          </motion.p>
        </div>

        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none pointer-events-none">
          <svg
            viewBox="0 0 1440 60"
            preserveAspectRatio="none"
            className="w-full h-10 sm:h-14"
          >
            <path
              d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z"
              className="fill-white dark:fill-black"
            />
          </svg>
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────────────── */}
      <section className="py-16 w-full bg-white dark:bg-black">
        <div className="max-w-5xl mx-auto grid gap-6 md:grid-cols-3 px-6">
          {[
            {
              icon: Search,
              titleKey: 'aboutUs.find.title',
              textKey: 'aboutUs.find.text',
            },
            {
              icon: CalendarCheck,
              titleKey: 'aboutUs.book.title',
              textKey: 'aboutUs.book.text',
            },
            {
              icon: Settings,
              titleKey: 'aboutUs.manage.title',
              textKey: 'aboutUs.manage.text',
            },
          ].map(({ icon: Icon, titleKey, textKey }, i) => (
            <motion.div
              key={titleKey}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              whileHover={{ y: -4 }}
              className="group flex flex-col p-8 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e] shadow-sm hover:shadow-md hover:border-gray-300 dark:hover:border-white/20 transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-xl bg-[#103254]/6 dark:bg-white/10 flex items-center justify-center mb-5 group-hover:bg-[#103254]/10 dark:group-hover:bg-white/15 transition-colors">
                <Icon className="w-5 h-5 text-[#103254] dark:text-white" aria-hidden />
              </div>
              <h2 className="text-base font-semibold text-[#103254] dark:text-white mb-2">
                {t(titleKey)}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {t(textKey)}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Gestión + Misión ──────────────────────────────────────────────── */}
      <section className="bg-gray-50 dark:bg-[#1c1c1e] py-20 w-full">
        <div className="max-w-3xl mx-auto px-6 text-center space-y-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl font-semibold mb-4 text-[#103254] dark:text-white">
              {t('aboutUs.managementTitle')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
              {t('aboutUs.managementText')}
            </p>
          </motion.div>

          <div className="h-px bg-gray-200 dark:bg-white/10 mx-auto max-w-xs" />

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h2 className="text-3xl font-semibold mb-4 text-[#288E7F]">
              {t('aboutUs.missionTitle')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
              {t('aboutUs.missionText')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── CTA banner ────────────────────────────────────────────────────── */}
      <section
        className="py-16 w-full text-center text-white"
        style={{
          background: 'linear-gradient(135deg, #288E7F 0%, #1a6e63 100%)',
        }}
      >
        <motion.div
          className="max-w-2xl mx-auto px-6"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl md:text-3xl font-semibold mb-3">
            {t('aboutUs.ctaTitle')}{' '}
            <span className="text-white/80">
              {t('aboutUs.ctaTitleHighlight')}
            </span>
          </h2>
          <p className="text-white/70 leading-relaxed">
            {t('aboutUs.ctaText')}
          </p>
        </motion.div>
      </section>

      {/* ── Video carousel ────────────────────────────────────────────────── */}
      <section className="bg-white dark:bg-black py-20 w-full">
        <motion.div
          className="max-w-3xl mx-auto px-6 text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {videosLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="w-8 h-8 rounded-full border-2 border-[#103254]/20 dark:border-white/10 border-t-[#103254] dark:border-t-white animate-spin" />
            </div>
          ) : videos.length > 0 ? (
            <>
              {/* Título y descripción animados */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={current}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.25 }}
                  className="mb-12"
                >
                  <h3 className="text-2xl font-semibold text-[#103254] dark:text-white mb-4">
                    {videos[current].titulo}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 leading-relaxed max-w-xl mx-auto">
                    {videos[current].descripcion}
                  </p>
                </motion.div>
              </AnimatePresence>

              {/* Carrusel */}
              <div className="flex items-center justify-center gap-4">
                {videos.length > 1 && (
                  <button
                    onClick={prev}
                    aria-label="Vídeo anterior"
                    className="flex-shrink-0 w-10 h-10 rounded-full border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e] shadow-sm flex items-center justify-center text-gray-500 dark:text-gray-300 hover:text-[#103254] dark:hover:text-white hover:border-[#103254]/30 dark:hover:border-white/20 hover:shadow-md transition-all"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}

                <div className="flex flex-col items-center gap-4 w-72">
                  <div
                    className="w-full rounded-xl overflow-hidden shadow-lg relative"
                    style={{ aspectRatio: '9/16' }}
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.iframe
                        key={current}
                        src={videos[current].youtube_embed.replace(
                          /\/\/(www\.)?youtube\.com\//,
                          '//www.youtube-nocookie.com/'
                        )}
                        title={videos[current].titulo}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                        className="absolute inset-0 w-full h-full"
                        initial={{ opacity: 0, x: direction * 40 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: direction * -40 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                      />
                    </AnimatePresence>
                  </div>

                  {videos.length > 1 && (
                    <div className="flex gap-2">
                      {videos.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => goTo(i, i > current ? 1 : -1)}
                          aria-label={`Ir al vídeo ${i + 1}`}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            i === current
                              ? 'bg-[#103254] dark:bg-white scale-125'
                              : 'bg-gray-300 dark:bg-white/20 hover:bg-gray-400 dark:hover:bg-white/30'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {videos.length > 1 && (
                  <button
                    onClick={next}
                    aria-label="Vídeo siguiente"
                    className="flex-shrink-0 w-10 h-10 rounded-full border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e] shadow-sm flex items-center justify-center text-gray-500 dark:text-gray-300 hover:text-[#103254] dark:hover:text-white hover:border-[#103254]/30 dark:hover:border-white/20 hover:shadow-md transition-all"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            </>
          ) : null}
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;